<template>
  <searchable-select
    v-bind="$attrs"
    :clearable="!$attrs.readonly"
    outlined
    dense
    return-object
    light
    :hide-details="hideDetails"
    item-value="id"
    @change="value => this.$emit('change', value)"
    :item-text="item => [item.name].filter(Boolean).join(' ')"
    :search-text="searchText"
    :search-placeholder="searchPlaceholder || $t('items.search')"
    :items="listItems"
    :label="label"
    :disabled="saving || disabled"
    :value="value"
    :multiple="multiple"
    :rules="[required && $rules.required].filter(Boolean)"
    @menu="onMenu"
  >
    <template v-if="filterTypes.length > 1" v-slot:filters>
      <item-type-chips v-model="typeFilter" :types="filterTypes" class="px-4" />
    </template>
    <template v-slot:selection="{ item, attrs, on }">
      <span v-on="on" v-bind="attrs" :key="item.id">
        {{ item.name }}
        <!-- Tyyppi jää näkyviin valinnan jälkeen, kun valittavana on useampi tyyppi. -->
        <span v-if="showType" class="grey--text text--darken-1 ml-1">{{ typeLabel(item.typeId) }}</span>
      </span>
    </template>
    <template v-slot:item="data">
      <v-list-item v-on="data.on" v-bind="data.attrs" :key="data.item.id">
        <v-list-item-content>
          <v-list-item-title>
            {{ data.item.name }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-slot:prepend>
      <slot name="prepend"></slot>
    </template>
  </searchable-select>
</template>

<script>
import itemApi from '@/api/item';
import ItemTypeChips from '@/components/ItemTypeChips';
import SearchableSelect from '@/components/SearchableSelect';
import { itemType, loadItemTypes, typeOrder, typePluralName } from '@/helpers/itemTypes';

/**
 * Yhteinen nimikevalinta. Arvo on nimikeobjekti (return-object), tapahtuma `change`.
 * Ilman `items`-proppia nimikkeet ladataan itse välimuistista ja rajataan `types`-propilla.
 * Lista ryhmitellään tyypeittäin, kun tyyppejä on useampi, ja haku kattaa nimen, koodin ja tyypin.
 */
export default {
  name: 'ItemCombobox',
  components: { ItemTypeChips, SearchableSelect },
  model: { prop: 'value', event: 'change' },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      required: true,
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    saving: Boolean,
    // Kutsujan oma lista ohittaa latauksen.
    items: {
      type: Array,
      default: null,
    },
    // Tyyppirajaus nimiketyyppien id:inä (helpers/itemTypes.js); null = kaikki, [] = ei yhtään.
    types: {
      type: Array,
      default: null,
    },
    value: [Object, Array],
    required: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: false,
    },
    groupByType: {
      type: Boolean,
      default: true,
    },
    // Yksityyppisissä valinnoissa osuvampi teksti, esim. "Hae työtehtävistä".
    searchPlaceholder: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loadedItems: [],
      // Valikon tyyppisuodatin (null = kaikki); nollataan, kun valikko sulkeutuu.
      typeFilter: null,
    };
  },
  computed: {
    typedItems() {
      const items = this.items || this.loadedItems;
      if (this.types) return items.filter(item => this.types.includes(item.typeId));
      if (this.items) return items;
      // Itse ladatuista jätetään pois käytöstä poistettujen tyyppien nimikkeet.
      return items.filter(item => itemType(item.typeId)?.active !== false);
    },
    // Tyypit, joissa on nimikkeitä, Nimikkeet-näkymän järjestyksessä.
    filterTypes() {
      if (!this.groupByType) return [];
      // Tyypittömät nimikkeet näkyvät vain Kaikki-rajauksessa (pillerin arvo null on Kaikki).
      const types = [...new Set(this.typedItems.map(item => item.typeId))].filter(type => type != null);
      return types.sort((a, b) => typeOrder(a) - typeOrder(b));
    },
    showType() {
      return !this.multiple && this.filterTypes.length > 1;
    },
    listItems() {
      // Valittu nimike, jota ei ole listassa (esim. passivoitu tai suodatettu pois), pidetään mukana.
      const values = Array.isArray(this.value) ? this.value : this.value ? [this.value] : [];
      const filtered = this.typeFilter ? this.typedItems.filter(item => item.typeId === this.typeFilter) : this.typedItems;
      const missing = values.filter(v => v && !filtered.some(item => item.id === v.id));
      const items = [...missing, ...filtered];
      const types = [...new Set(items.map(item => item.typeId))];
      if (!this.groupByType || types.length < 2) return items;

      // Ryhmät Nimikkeet-näkymän järjestyksessä, nimikkeet ryhmän sisällä nimen mukaan.
      const sorted = [...items].sort(
        (a, b) => typeOrder(a.typeId) - typeOrder(b.typeId) || (a.name || '').localeCompare(b.name || '', 'fi')
      );
      const result = [];
      let lastType;
      sorted.forEach(item => {
        if (item.typeId !== lastType) {
          result.push({ header: this.typeLabel(item.typeId) });
          lastType = item.typeId;
        }
        result.push(item);
      });
      return result;
    },
  },
  async created() {
    try {
      const [items] = await Promise.all([this.items ? null : itemApi.getItemsCached(), loadItemTypes()]);
      if (items) this.loadedItems = items;
    } catch (error) {
      console.error('Error loading items:', error);
    }
  },
  methods: {
    onMenu(open) {
      if (!open) this.typeFilter = null;
    },
    typeLabel(typeId) {
      return typePluralName(typeId);
    },
    searchText(item) {
      return [item.name, item.code, this.typeLabel(item.typeId)].filter(Boolean).join(' ');
    },
  },
};
</script>

<style scoped></style>
